import { api, unwrap, type APIResponse } from "./client";

// Mirrors app/routers/queue.py QueueMemberOut. One row = one WebRTC agent
// extension assigned to a campaign's transfer queue (queue_member table).
export interface QueueMember {
  queue_member_id: number;
  campaign_id: number;
  extension_id: number;
  extension_name: string;
  extension_title: string | null;
  wrapup_time: number;
  created_at: string;
  // True when this row currently satisfies the same predicate the `members`
  // realtime view (migrations 0017/0042) requires -- i.e. Asterisk would
  // actually ring this member right now. A row can exist and still be
  // `false` (forward_to set, or the extension soft-deleted, after it was
  // added) -- see app/routers/queue.py's module docstring.
  valid: boolean;
}

export async function listQueueMembers(campaignId: number): Promise<QueueMember[]> {
  const resp = await api.get<APIResponse<QueueMember[]>>(`/queues/${campaignId}/members`);
  return unwrap(resp.data);
}

export async function addQueueMember(
  campaignId: number,
  extensionId: number,
): Promise<QueueMember> {
  const resp = await api.post<APIResponse<QueueMember>>(`/queues/${campaignId}/members`, {
    extension_id: extensionId,
  });
  return unwrap(resp.data);
}

export async function removeQueueMember(campaignId: number, extensionId: number): Promise<void> {
  await api.delete(`/queues/${campaignId}/members`, { params: { extension_id: extensionId } });
}

// Mirrors app/routers/queue.py EligibleCampaignOut. A campaign the caller owns
// that has >=1 valid agent — i.e. one an inbound DID may be routed to.
export interface EligibleCampaign {
  campaign_id: number;
  name: string;
  agent_count: number;
}

export async function listEligibleCampaigns(): Promise<EligibleCampaign[]> {
  const resp = await api.get<APIResponse<EligibleCampaign[]>>("/queues/eligible-campaigns");
  return unwrap(resp.data);
}

// --- Live staffing -------------------------------------------------------
// Membership (above) is "who is assigned". These answer "who can actually
// take a transfer this second" -- the two are routinely different, and the
// gap is invisible everywhere else in the UI.

/** One queue member and what they are doing right now.
 *
 * `status` is derived server-side (app/routers/queue.py `_agent_from_row`) so
 * every screen renders the same agent identically, and so it can never
 * disagree with the `ictbroadcast.queue_members` realtime view that Asterisk
 * reads when choosing whom to ring. */
export interface QueueAgentLive {
  extension_id: number;
  extension_name: string;
  extension_title: string | null;
  valid: boolean;
  signed_in: boolean;
  on_call: boolean;
  paused: boolean;
  ring_unreachable: boolean;
  status: "offline" | "unreachable" | "on break" | "on a call" | "ready";
  time_login: string | null;
  calls_taken: number;
  ring_no_answer: number;
  last_call_at: string | null;
  /** Rung repeatedly, answered nothing. The platform still calls such a seat
   * "available" — presence and reachability are different things — so without
   * this a phone ringing into the void looks identical to a working one. */
  ringing_but_never_answers: boolean;
  /** Signed in, the queue was connecting calls to their colleagues, and this
   * seat was never even tried — a queue skips an unreachable member silently,
   * so the agent looks ready while receiving nothing. */
  never_offered_a_call: boolean;
}

export interface QueueLive {
  campaign_id: number;
  campaign_name: string;
  /** The name Asterisk calls, for comparing against `queue show <name>`. */
  queue_name: string;
  members: number;
  valid_members: number;
  signed_in: number;
  ready: number;
  on_call: number;
  paused: number;
  /** Nobody ready and nobody mid-call — a press-1 caller rings out. */
  no_one_taking_calls: boolean;
  entered: number;
  connected: number;
  abandoned: number;
  /** Share of callers who hung up waiting — the number that says whether the
   * dialer is outpacing the agents. */
  abandon_pct: number;
  avg_hold_s: number;
  agents: QueueAgentLive[];
}

/** Staffing for every queue the caller can see. Admins get all queues;
 * everyone else only campaigns they created. */
export async function listQueuesLive(): Promise<QueueLive[]> {
  const resp = await api.get<APIResponse<QueueLive[]>>("/queues/live");
  return unwrap(resp.data);
}

export async function getQueueLive(campaignId: number): Promise<QueueLive> {
  const resp = await api.get<APIResponse<QueueLive>>(`/queues/${campaignId}/live`);
  return unwrap(resp.data);
}

// --- Per-agent membership --------------------------------------------------
// The same queue_member rows as above, read and written per AGENT: "move
// Nelson from inbound to outbound" is one call with the full wanted set.

/** A campaign the caller can assign agents to, with its roster size. Includes
 * empty queues on purpose — an unstaffed queue is the one to staff. */
export interface QueueCampaign {
  campaign_id: number;
  name: string;
  status: string;
  members: number;
  valid_members: number;
}

export interface QueueMembership {
  queue_member_id: number;
  campaign_id: number;
  campaign_name: string;
  extension_id: number;
  extension_name: string;
  extension_title: string | null;
  /** Same predicate as the `members` realtime view — false means Asterisk
   * will not ring this member no matter what the roster says. */
  valid: boolean;
  /** Why, when `valid` is false. Surfaced, never hidden. */
  invalid_reason: string | null;
}

export async function listQueueCampaigns(): Promise<QueueCampaign[]> {
  const resp = await api.get<APIResponse<QueueCampaign[]>>("/queues/campaigns");
  return unwrap(resp.data);
}

export async function listQueueMemberships(): Promise<QueueMembership[]> {
  const resp = await api.get<APIResponse<QueueMembership[]>>("/queues/memberships");
  return unwrap(resp.data);
}

/** Set exactly which queues one seat takes calls from. Returns the seat's
 * resulting memberships. */
export async function setAgentQueueMemberships(
  extensionId: number,
  campaignIds: number[],
): Promise<QueueMembership[]> {
  const resp = await api.put<APIResponse<QueueMembership[]>>(
    `/queues/agents/${extensionId}/memberships`,
    { campaign_ids: campaignIds },
  );
  return unwrap(resp.data);
}

/** Sign one seat out of the queue from the floor board.
 *
 * The idle sweep gets there within five minutes on its own; this is for the
 * manager who is already looking at the board and can see the seat is dead.
 */
export async function signOutSeat(extensionId: number): Promise<{ signed_out: boolean; title?: string }> {
  const resp = await api.post<APIResponse<{ signed_out: boolean; title?: string }>>(
    "/queues/agents/sign-out",
    { extension_id: extensionId },
  );
  return unwrap(resp.data);
}
